import { useCachedData } from '../hooks/useCachedData';
import { endpoints } from '../api/config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, Box, Grid, Typography, Tooltip, Snackbar, Alert, CircularProgress, TextField, InputAdornment, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getDateInputProps } from '../utils/dateConstraints';
import {
  BUTTON_STYLE,
  DIALOG_CLOSE_BUTTON_STYLE,
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
  NEAR_FULLSCREEN_DIALOG_PAPER_SX,
  BUTTON_VARIANT,
  BUTTON_SIZE
} from '../constants/styles';

// Helper function to flatten nested objects and arrays
function flattenObject(obj, prefix = '') {
  const flattened = {};
  
  Object.entries(obj).forEach(([key, value]) => {
    const newKey = prefix ? `${prefix}_${key}` : key;
    
    if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        // Handle arrays by stringifying them
        flattened[newKey] = JSON.stringify(value);
      } else {
        // For netsuite_response object, preserve its structure
        if (key === 'netsuite_response') {
          flattened[newKey] = value;
        } else {
          // Recursively flatten other nested objects
          Object.assign(flattened, flattenObject(value, newKey));
        }
      }
    } else {
      // Handle primitive values
      flattened[newKey] = value;
    }
  });
  
  return flattened;
}

// Helper function to convert object to table format
function convertToTableFormat(data) {
  if (!data || typeof data !== 'object') return [];
  
  // If data is an array, process each item
  if (Array.isArray(data)) {
    return data.map(item => convertToTableFormat(item)[0]);
  }
  
  // For single object, create a row with all properties
  const row = {};
  
  function processObject(obj, prefix = '') {
    Object.entries(obj).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}_${key}` : key;
      
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Store the object as is for netsuite_response
        if (key === 'netsuite_response' || newKey.endsWith('_netsuite_response')) {
          row[newKey] = value;
        } else {
          // Recursively flatten other nested objects
          processObject(value, newKey);
        }
      } else if (Array.isArray(value)) {
        // Store the array as is for itemData
        if (key === 'itemData' || newKey.endsWith('_itemData')) {
          row[newKey] = value;
        } else {
          // Handle other arrays by joining their values
          row[newKey] = value.join(', ');
        }
      } else {
        // Handle primitive values
        row[newKey] = value;
      }
    });
  }
  
  processObject(data);
  return [row];
}

// Helper function to format column header
const formatColumnHeader = (key) => {
  // Special handling for username-related fields
  if (key.toLowerCase() === 'username' || key.toLowerCase() === 'username') {
    return 'Username';
  }
  if (key.toLowerCase() === 'username') {
    return 'Username';
  }
  
  return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Helper function to create base column configuration
const createBaseColumn = (key) => ({
  accessorKey: key,
  header: formatColumnHeader(key),
  minSize: COLUMN_MIN_SIZE,
  size: COLUMN_SIZE,
});

const BinCountRecords = () => {
  // Default date range to today's date in YYYY-MM-DD
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem('bin-count-start-date');
    return saved || todayStr;
  });
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem('bin-count-end-date');
    return saved || todayStr;
  });
  const [successFilter, setSuccessFilter] = useState(() => {
    const saved = localStorage.getItem('bin-count-success-filter');
    return saved || 'all'; // all | success | errors
  });

  // Build endpoint with optional date range; by default this sends today's date range
  const binCountEndpointWithRange = useMemo(() => {
    const base = endpoints.binCountRecords;
    // If both dates selected, send as range
    if (startDate && endDate) {
      return `${base}?start_date=${startDate}&end_date=${endDate}`;
    }
    // Fallbacks if only one side of range is provided
    if (startDate) {
      return `${base}?start_date=${startDate}`;
    }
    if (endDate) {
      return `${base}?end_date=${endDate}`;
    }
    // If no dates, fall back to all data
    return base;
  }, [startDate, endDate]);

  const { data, isLoading, error, refetch } = useCachedData(binCountEndpointWithRange);
  const [open, setOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [binsOpen, setBinsOpen] = useState(false);
  const [binsDetails, setBinsDetails] = useState(null);
  const [itemDataOpen, setItemDataOpen] = useState(false);
  const [itemDataLoading, setItemDataLoading] = useState(false);
  const [itemDataDetails, setItemDataDetails] = useState(null);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Main table column visibility
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-count-records-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Bin data table column visibility
  const [binDataColumnVisibility, setBinDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-count-records-bin-data-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Bins data table column visibility
  const [binsDataColumnVisibility, setBinsDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-count-records-bins-data-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Item data table column visibility
  const [itemDataColumnVisibility, setItemDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-count-records-item-data-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });



  // Save column visibility states to localStorage
  useEffect(() => {
    localStorage.setItem('bin-count-records-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-count-records-bin-data-column-visibility', JSON.stringify(binDataColumnVisibility));
  }, [binDataColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-count-records-bins-data-column-visibility', JSON.stringify(binsDataColumnVisibility));
  }, [binsDataColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-count-records-item-data-column-visibility', JSON.stringify(itemDataColumnVisibility));
  }, [itemDataColumnVisibility]);

  // Save date range and success filter to localStorage
  useEffect(() => {
    localStorage.setItem('bin-count-start-date', startDate);
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem('bin-count-end-date', endDate);
  }, [endDate]);

  useEffect(() => {
    localStorage.setItem('bin-count-success-filter', successFilter);
  }, [successFilter]);



  // Process the data to handle bin_data and bins_data
  const processedData = useMemo(() => {
    if (!data) return [];
    return data
      .map(record => {
        let binData = record.bin_data ? convertToTableFormat(record.bin_data) : [];
        let username = '';
        
        // Enhanced username extraction from multiple sources
        if (record.username) {
          username = record.username;
        } else if (record.UserName) {
          username = record.UserName;
        } else if (record.user) {
          username = record.user;
        } else if (record.User) {
          username = record.User;
        } else if (record.user_id) {
          username = record.user_id;
        } else if (record.userid) {
          username = record.userid;
        } else if (record.created_by) {
          username = record.created_by;
        } else if (record.modified_by) {
          username = record.modified_by;
        } else if (record.operator) {
          username = record.operator;
        } else if (record.operator_id) {
          username = record.operator_id;
        } else if (binData && binData.length > 0) {
          // Check for username in binData
          if (binData[0].username) {
            username = binData[0].username;
            // Remove username from binData
            delete binData[0].username;
          } else if (binData[0].UserName) {
            username = binData[0].UserName;
            // Remove UserName from binData
            delete binData[0].UserName;
          } else if (binData[0].user) {
            username = binData[0].user;
            // Remove user from binData
            delete binData[0].user;
          } else if (binData[0].User) {
            username = binData[0].User;
            // Remove User from binData
            delete binData[0].User;
          } else if (binData[0].user_id) {
            username = binData[0].user_id;
            // Remove user_id from binData
            delete binData[0].user_id;
          } else if (binData[0].userid) {
            username = binData[0].userid;
            // Remove userid from binData
            delete binData[0].userid;
          } else if (binData[0].created_by) {
            username = binData[0].created_by;
            // Remove created_by from binData
            delete binData[0].created_by;
          } else if (binData[0].modified_by) {
            username = binData[0].modified_by;
            // Remove modified_by from binData
            delete binData[0].modified_by;
          } else if (binData[0].operator) {
            username = binData[0].operator;
            // Remove operator from binData
            delete binData[0].operator;
          } else if (binData[0].operator_id) {
            username = binData[0].operator_id;
            // Remove operator_id from binData
            delete binData[0].operator_id;
          }
        }
        
        // Deep search for username in nested objects if still not found
        if (!username) {
          const findUsernameInObject = (obj, path = '') => {
            if (!obj || typeof obj !== 'object') return null;
            
            for (const [key, value] of Object.entries(obj)) {
              const currentPath = path ? `${path}.${key}` : key;
              
              // Check if this key contains username-related terms
              if (key.toLowerCase().includes('user') || key.toLowerCase().includes('operator') || key.toLowerCase().includes('created_by') || key.toLowerCase().includes('modified_by')) {
                if (typeof value === 'string' && value.trim()) {
                  return value.trim();
                } else if (value && typeof value === 'object' && value.username) {
                  return value.username;
                }
              }
              
              // Recursively search nested objects (but avoid infinite recursion)
              if (value && typeof value === 'object' && !Array.isArray(value) && currentPath.split('.').length < 3) {
                const found = findUsernameInObject(value, currentPath);
                if (found) return found;
              }
            }
            return null;
          };
          
          const foundUsername = findUsernameInObject(record);
          if (foundUsername) {
            username = foundUsername;
          }
        }
        

        
        const binsData = record.bins_data ? convertToTableFormat(record.bins_data) : [];
        // Only include bins_data if it has data
        const processedRecord = {
          ...record,
          username, // Add username to main table
          bin_data: binData,
          // Ensure netsuite_acknowledgement_success is a boolean
          netsuite_acknowledgement_success: Boolean(record.netsuite_acknowledgement_success)
        };
        if (binsData && binsData.length > 0) {
          processedRecord.bins_data = binsData;
        }
        return processedRecord;
      })
      .sort((a, b) => {
        // Sort by created_at timestamp in descending order (newest first)
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return dateB - dateA;
      });
  }, [data]);

  // Apply success filter (All / Success / Errors)
  const filteredData = useMemo(() => {
    if (!processedData.length) return [];
    if (successFilter === 'all') return processedData;

    return processedData.filter((record) => {
      const success = record?.netsuite_response?.netsuite_acknowledgment?.success;
      const isSuccess =
        success === true || success === 'Yes' || success === 'yes' || success === 1 || success === '1';
      const isFailure =
        success === false || success === 'No' || success === 'no' || success === 0 || success === '0';

      if (successFilter === 'success') {
        return isSuccess;
      }
      if (successFilter === 'errors') {
        return isFailure;
      }
      return true;
    });
  }, [processedData, successFilter]);

  // Generate main columns dynamically with fixed order
  const columns = useMemo(() => {
    if (!processedData.length) return [];
    
    const baseColumns = [];

    // 1. ID column (first column from data)
    const keys = Object.keys(processedData[0]).filter(key => 
      !['bin_data', 'bins_data', 'netsuite_acknowledgement_success', 'created_at', 'updated_at', 'username'].includes(key)
    );
    
    if (keys[0]) {
      baseColumns.push({
        ...createBaseColumn(keys[0]),
        cell: info => info.getValue()
      });
    }

    // 2. Username column
    baseColumns.push({
      ...createBaseColumn('username'),
      header: 'Username',
      cell: info => {
        const value = info.getValue();
        
        // Handle different data types
        if (value === null || value === undefined || value === '') {
          return '-';
        }
        
        // Convert to string and trim
        const stringValue = String(value).trim();
        return stringValue || '-';
      },
      size: 150,
    });

    // 3. Bin Name column
    baseColumns.push({
      ...createBaseColumn('bin_name'),
      header: 'Bin Name',
      accessorFn: (row) => {
        const binData = row.bin_data?.[0] || {};
        return binData.binName || '-';
      },
      size: 150,
    });



    // 5. Inventory ID column
    baseColumns.push({
      ...createBaseColumn('inventory_id'),
      header: 'Inventory ID',
      accessorFn: (row) => {
        const netsuiteResponse = row.netsuite_response?.netsuite_response || row.netsuite_response;
        const inventoryId = netsuiteResponse?.netsuite_acknowledgment?.inventoryAdjustmentId;
        return inventoryId || '-';
      },
      size: 150,
    });

    // 6. Bin ID column (if exists in data)
    if (processedData[0].bin_id) {
      baseColumns.push({
        ...createBaseColumn('bin_id'),
        header: 'Bin ID',
        cell: info => info.getValue(),
        size: 150,
      });
    }

    // 7. Bin Data column with status indicator
    baseColumns.push({
      ...createBaseColumn('bin_data'),
      header: 'Bin Data',
              cell: info => {
          const value = info.getValue();
          const hasData = Array.isArray(value) ? value.length > 0 : (value && typeof value === 'object' && Object.keys(value).length > 0);
          let success;
          let original;
          try {
            original = info.row?.original;
            success = original?.netsuite_response?.netsuite_acknowledgment?.success;
          } catch {}
          
          // Handle both boolean and string values for success
          const isSuccess = success === true || success === 'Yes' || success === 'yes';
          const isFailure = success === false || success === 'No' || success === 'no';
          
          // If we have NetSuite response data but success is undefined, show as failure (red dot)
          const hasNetSuiteResponse = original?.netsuite_response;
          const shouldShowFailure = isFailure || (hasNetSuiteResponse && success === undefined);
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: isSuccess ? '#4caf50' : (shouldShowFailure ? '#e53935' : 'transparent') }} />
              <Typography variant="body2">{hasData ? 'View Details' : 'No Data'}</Typography>
            </Box>
          );
        },
    });

    // 8. Created At column
    if (processedData[0].created_at) {
      baseColumns.push({
        ...createBaseColumn('created_at'),
        header: 'Created At',
        cell: info => info.getValue(),
        size: 180,
      });
    }

    // 9. Updated At column
    if (processedData[0].updated_at) {
      baseColumns.push({
        ...createBaseColumn('updated_at'),
        header: 'Updated At',
        cell: info => info.getValue(),
        size: 180,
      });
    }

    // Add any remaining columns that weren't in the fixed order
    keys.slice(1).forEach(key => {
      if (!['netsuite_response', 'username', 'bin_id', 'inventory_id'].includes(key)) {
        const columnExists = baseColumns.some(col => col.accessorKey === key);
        if (!columnExists) {
          baseColumns.push({
            ...createBaseColumn(key),
            cell: info => info.getValue()
          });
        }
      }
    });

    return baseColumns;
  }, [processedData]);

  // Generate detail columns dynamically
  const detailColumns = useMemo(() => {
    if (!details || !details.length) return [];
    // Get all unique keys from the first detail record, excluding username
    const keys = Object.keys(details[0]).filter(key => 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'user'
    );
    return keys.map(key => {
      // For the location column, allow wrapping
      if (key.toLowerCase().includes('location')) {
        return {
          ...createBaseColumn(key),
          cell: info => info.getValue(),
          minSize: 250,
          size: 300,
          maxSize: 600,
          meta: { wrap: true }
        };
      }
      return {
        ...createBaseColumn(key),
        cell: info => {
          const value = info.getValue();
          if ((key === 'itemData' || key.endsWith('_itemData')) && Array.isArray(value)) {
            return `${value.length} items`;
          }
          if ((key === 'netsuite_response' || key.endsWith('_netsuite_response')) && value && typeof value === 'object') {
            const netsuiteResponse = value.netsuite_response || value;
            const acknowledgment = netsuiteResponse.netsuite_acknowledgment || {};
            const success = acknowledgment.success;
            return (
              <Button
                variant={BUTTON_VARIANT}
                size={BUTTON_SIZE}
                sx={{
                  ...BUTTON_STYLE,
                  backgroundColor: success ? '#81c784' : '#ef9a9a',
                  '&:hover': {
                    backgroundColor: success ? '#66bb6a' : '#e57373',
                  }
                }}
                onClick={() => {
                  setNetsuiteDetails(value);
                  setNetsuiteOpen(true);
                }}
              >
                View Response
              </Button>
            );
          }
          return value;
        },
      };
    });
  }, [details]);

  // Generate bins detail columns dynamically
  const binsDetailColumns = useMemo(() => {
    if (!binsDetails || !binsDetails.length) return [];
    
    // Get all unique keys from the first bins detail record, excluding username
    const keys = Object.keys(binsDetails[0]).filter(key => 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'user'
    );
    
    return keys.map(key => ({
      ...createBaseColumn(key),
      cell: info => {
        const value = info.getValue();
        if ((key === 'itemData' || key.endsWith('_itemData')) && Array.isArray(value)) {
          return `${value.length} items`;
        }
        if ((key === 'netsuite_response' || key.endsWith('_netsuite_response')) && value && typeof value === 'object') {
          const netsuiteResponse = value.netsuite_response || value;
          const acknowledgment = netsuiteResponse.netsuite_acknowledgment || {};
          const success = acknowledgment.success;
          return (
            <Button
              variant={BUTTON_VARIANT}
              size={BUTTON_SIZE}
              sx={{
                ...BUTTON_STYLE,
                backgroundColor: success ? '#81c784' : '#ef9a9a',
                '&:hover': {
                  backgroundColor: success ? '#66bb6a' : '#e57373',
                }
              }}
              onClick={() => {
                setNetsuiteDetails(value);
                setNetsuiteOpen(true);
              }}
            >
              View Response
            </Button>
          );
        }
        return value;
      },
    }));
  }, [binsDetails]);

  // Generate item data columns dynamically
  const itemDataColumns = useMemo(() => {
    if (!itemDataDetails || !itemDataDetails.length) return [];
    
    // Get all unique keys from the first item, excluding username
    const keys = Object.keys(itemDataDetails[0]).filter(key => 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'username' && 
      key.toLowerCase() !== 'user'
    );
    
    return keys.map(key => createBaseColumn(key));
  }, [itemDataDetails]);



  // Handle refresh click
  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const binCountRecordsMainGrid = useDataGridProTable({
    data: filteredData,
    loading: isLoading,
    error: error ? (error.message || 'Failed to load bin count records') : null,
    title: 'Bin Count Records',
    columns,
    onDataRefresh: handleRefreshClick,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      if (!row || !row.bin_data) return;
      setOpen(true);
      setDetailsLoading(true);
      setTimeout(() => {
        setDetails(row.bin_data);
        setDetailsLoading(false);
      }, 100);
    },
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
    initialSorting: [{ id: 'created_at', desc: true }],
    actionButtons: [
      <Box key="bin-count-filters" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Refresh Data">
          <IconButton
            color="primary"
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            sx={{ ml: 1 }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
        </Tooltip>
        <TextField
          select
          label="View"
          size="small"
          value={successFilter}
          onChange={(e) => setSuccessFilter(e.target.value)}
          sx={{
            minWidth: 140,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
          <MenuItem value="success" sx={{ fontSize: '0.8125rem' }}>Success</MenuItem>
          <MenuItem value="errors" sx={{ fontSize: '0.8125rem' }}>Errors</MenuItem>
        </TextField>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
      </Box>,
    ],
  });

  const binCountBinDataRowGrid = useDataGridProTable({
    data: details || [],
    loading: false,
    columns: detailColumns,
    title: 'bin-count-bin-row',
    initialColumnVisibility: binDataColumnVisibility,
    onColumnVisibilityChange: setBinDataColumnVisibility,
    onRowClick: (row) => {
      if (!row) return;
      const value = row.itemData || row.ItemData || row.item_data;
      if (Array.isArray(value) && value.length > 0) {
        setItemDataOpen(true);
        setItemDataLoading(true);
        setTimeout(() => {
          setItemDataDetails(value);
          setItemDataLoading(false);
        }, 100);
      }
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    error: null,
  });

  const binCountBinsDataGrid = useDataGridProTable({
    data: binsDetails || [],
    title: 'Bins Data Details',
    columns: binsDetailColumns,
    initialColumnVisibility: binsDataColumnVisibility,
    onColumnVisibilityChange: setBinsDataColumnVisibility,
    onRowClick: (row) => {
      if (!row) return;
      const value = row.itemData || row.ItemData || row.item_data;
      if (Array.isArray(value) && value.length > 0) {
        setItemDataOpen(true);
        setItemDataLoading(true);
        setTimeout(() => {
          setItemDataDetails(value);
          setItemDataLoading(false);
        }, 100);
      }
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    loading: false,
    error: null,
  });

  const binCountItemDataGrid = useDataGridProTable({
    data: itemDataDetails || [],
    title: 'Item Data Details',
    columns: itemDataColumns,
    initialColumnVisibility: itemDataColumnVisibility,
    onColumnVisibilityChange: setItemDataColumnVisibility,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    loading: false,
    error: null,
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface hook={binCountRecordsMainGrid} DataGridPro={DataGridPro} />
      </Box>
      {/* Bin Data Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          Bin Data Details
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : details && (
            <>
              {/* NetSuite Response Details */}
              {(() => {
                // Get the NetSuite response from the main record that was clicked
                const mainRecord = processedData.find(record => 
                  record.bin_data === details || 
                  (Array.isArray(record.bin_data) && record.bin_data.length > 0 && record.bin_data[0] === details[0])
                );
                
                if (!mainRecord) return null;
                
                const nsObject = mainRecord.netsuite_response;
                if (!nsObject) return null;
                
                const ns = nsObject.netsuite_response || nsObject;
                const ack = ns?.netsuite_acknowledgment || {};
                const safe = (v) => {
                  if (v === null || v === undefined) return '-';
                  if (typeof v === 'object') return JSON.stringify(v);
                  return String(v);
                };
                return (
                  <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      NetSuite Response Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, rowGap: 0.5, columnGap: 2 }}>
                      <Typography variant="body2" color="text.secondary">Message</Typography>
                      <Typography variant="body2">{safe(ns?.message)}</Typography>
                      <Typography variant="body2" color="text.secondary">Operation Time</Typography>
                      <Typography variant="body2">{safe(ns?.operation_time)}</Typography>
                      <Typography variant="body2" color="text.secondary">Success</Typography>
                      <Typography variant="body2">{ack?.success ? 'Yes' : 'No'}</Typography>
                      {ack?.inventoryAdjustmentId && (
                        <>
                          <Typography variant="body2" color="text.secondary">Inventory Adjustment ID</Typography>
                          <Typography variant="body2">{safe(ack.inventoryAdjustmentId)}</Typography>
                        </>
                      )}
                      {ack?.binTransferId && (
                        <>
                          <Typography variant="body2" color="text.secondary">Bin Transfer ID</Typography>
                          <Typography variant="body2">{safe(ack.binTransferId)}</Typography>
                        </>
                      )}
                    </Box>
                    {Array.isArray(ack?.itemDetails) && ack.itemDetails.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant={BUTTON_VARIANT}
                          size={BUTTON_SIZE}
                          sx={BUTTON_STYLE}
                          onClick={() => {
                            // Open dialog immediately
                            setItemDataOpen(true);
                            setItemDataLoading(true);
                            
                            // Set data after a brief delay to show loading spinner
                            setTimeout(() => {
                              setItemDataDetails(ack.itemDetails);
                              setItemDataLoading(false);
                            }, 100);
                          }}
                        >
                          View Items ({ack.itemDetails.length})
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <MuiXDataGridSurface hook={binCountBinDataRowGrid} DataGridPro={DataGridPro} />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Bins Data Dialog */}
      <Dialog 
        open={binsOpen} 
        onClose={() => setBinsOpen(false)} 
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          Bins Data Details
          <IconButton
            aria-label="close"
            onClick={() => setBinsOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {binsDetails && binsDetails.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={binCountBinsDataGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            'No bins data available.'
          )}
        </DialogContent>
      </Dialog>
      {/* Item Data Dialog */}
      <Dialog 
        open={itemDataOpen} 
        onClose={() => setItemDataOpen(false)} 
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          Item Data Details
          <IconButton
            aria-label="close"
            onClick={() => setItemDataOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {itemDataDetails && itemDataDetails.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={binCountItemDataGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            'No item data available.'
          )}
        </DialogContent>
      </Dialog>

      {/* Add Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BinCountRecords; 